import {BRAND_CREATED, BRAND_UPDATED, BRANDS_FETCHED} from "./actionType";
import {Action} from "redux-actions";
import {OnFailure, OnSuccess, OnSuccessData} from "../actionHelpers";
import {api} from "../../../api/api";
import {Dispatch} from "react-redux";
import Brand from "../../../model/brand/Brand";
import {fetchOrganization} from "../organization/organization";
import {none, Option, option} from "ts-option";
import Address from "../../../model/address/Address";
import SiteData from "../../../model/site/SiteData";
import {siteCreated} from "../site/site";

/**
 * Brands fetched successful action.
 * @param {Array<Brand>} brands all brands from server.
 * @returns {Action<BRANDS_FETCHED>}
 */
function brandsFetched(brands: Array<Brand>): Action<BRANDS_FETCHED> {
	return {type: BRANDS_FETCHED, payload: {brands}};
}

/**
 * Brand created successful action.
 * @param {Brand} brand
 * @returns {Action<BRAND_CREATED>}
 */
function brandCreated(brand: Brand): Action<BRAND_CREATED> {
	return {type: BRAND_CREATED, payload: {brand}};
}

/**
 * Brand updated successful action.
 * @param {Brand} brand
 * @returns {Action<BRAND_UPDATED>}
 */
function brandUpdated(brand: Brand): Action<BRAND_UPDATED> {
	return {type: BRAND_UPDATED, payload: {brand}};
}

/**
 * Fetch brands from server.
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 * @return {(dispatch: Dispatch<any>) => void}
 */
export function fetchBrands(onSuccess: OnSuccessData<Brand[]>, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.brandApi.fetchBrands().then(brands => {
			dispatch(brandsFetched(brands));
			onSuccess(brands);
		}).catch(onFailure);
	};
}

/**
 * Create brand action.
 * Upload brand logo if given before creating.
 * @param {Brand} brand created.
 * @param {Option<File>} file optional logo of the brand to upload
 * @param {Option<Address>} address optional brand address.
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 * @return {(dispatch: Dispatch<any>) => void}
 */
export function createBrand(brand: Brand,
	file: Option<File>,
	address: Option<Address>,
	onSuccess: OnSuccessData<Brand>,
	onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		const create = (data: Brand) => {
			return api.instance.brandApi.createBrand(data).then(createdBrand => {
				dispatch(brandCreated(createdBrand));
				onSuccess(createdBrand);
			}).catch(onFailure);
		};
		const createWithSite = (data: Brand) => {
			if (address.isDefined) {
				return api.instance.siteApi.createSite(new SiteData(address.get)).then(site => {
					dispatch(siteCreated(site));
					return create(data.copy({siteId: option(site.id)}))
				}).catch(onFailure)
			}
			else {
				return create(data);
			}
		};
		if (file.isDefined) {
			return api.instance.attachmentApi.uploadFile(file.get).then(id => {
				return createWithSite(brand.copy({logoId: option(id)}));
			}).catch(onFailure)
		}
		else {
			return createWithSite(brand);
		}
	}
}

/**
 * Update brand action.
 * Upload brand logo if given before updating.
 * @param brandId
 * @param {Brand} brand created.
 * @param {Option<File>} file optional logo of the brand to upload
 * @param {Option<Address>} address optional brand address.
 * @param shouldRemoveLogo
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 * @return {(dispatch: Dispatch<any>) => void}
 */
export function updateBrand(brandId: string,
	brand: Brand,
	file: Option<File>,
	address: Option<Address>,
	shouldRemoveLogo: boolean,
	onSuccess: OnSuccessData<Brand>,
	onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		const update = (data: Brand) => {
			api.instance.brandApi.updateBrand(brandId, data).then(updatedBrand => {
				dispatch(brandUpdated(updatedBrand));
				onSuccess(updatedBrand);
			}).catch(onFailure);
		};

		const updateWithSite = (data: Brand) => {
			if (address.isDefined) {
				return api.instance.siteApi.createSite(new SiteData(address.get)).then(site => {
					dispatch(siteCreated(site));
					return update(data.copy({siteId: option(site.id)}))
				}).catch(onFailure)
			}
			else {
				return update(data);
			}
		};
		if (file.isDefined) {
			return api.instance.attachmentApi.uploadFile(file.get).then(id => {
				return updateWithSite(brand.copy({logoId: option(id)}));
			}).catch(onFailure)
		}
		else {
			return updateWithSite(brand.copy({logoId: shouldRemoveLogo ? none : brand.logoId}));
		}
	}
}

/**
 * Set brand as default action.
 * @param {Brand} brand set as default.
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 * @return {(dispatch: Dispatch<any>) => void}
 */
export function setBrandAsDefault(brand: Brand, onSuccess: OnSuccessData<Brand>, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.brandApi.setBrandAsDefault(brand.id).then(() => {
			dispatch(fetchOrganization(() => {}, () => {}));
			onSuccess(brand);
		}).catch(onFailure);
	}
}


export function fetchBrand(brandId: string, onSuccess: OnSuccessData<Brand>, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		dispatch(fetchBrands((brands) => {
			const [brand] = brands.filter(b => b.id == brandId);
			if (brand) {
				onSuccess(brand)
			}
			else {
				onFailure(new Error("Can not find brand"))
			}
		}, onFailure))
	}
}